import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Form, Button, Card, Row, Col, Accordion } from 'react-bootstrap';
import UkiesAssets from '../Admin/UkiesAssets';

export default function UkiesTextWithBlocks({ componentSettings, setComponentSettings, onChange, selectedComponent, setSelectedComponent, forms = [] }) {
    const [showAssetSelector, setShowAssetSelector] = useState(false);
    const [selectedBlockIndex, setSelectedBlockIndex] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [bodySourceCodeMode, setBodySourceCodeMode] = useState(false);
    const [blockDescriptionSourceCodeModes, setBlockDescriptionSourceCodeModes] = useState({});
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [dropIndicatorPosition, setDropIndicatorPosition] = useState(null); // 'before' or 'after'
    const [openBlockAccordions, setOpenBlockAccordions] = useState([]);
    const [preDragAccordionState, setPreDragAccordionState] = useState([]);

    const bodyEditorRef = useRef(null);
    const blockDescriptionRefs = useRef({});
    const apiToken = process.env.REACT_APP_UKIES_API_TOKEN;

    // Get current values with defaults
    const referenceId = componentSettings.reference_id || '';
    const strapline = componentSettings.strapline || '';
    const title = componentSettings.title || '';
    const body = componentSettings.body || '';
    const backgroundColor = componentSettings.background_color || 'white';
    const blockType = componentSettings.block_type || 'tile:var-1'; // Default to "Afbeelding met omhoogkomende tekst"
    const largeRowCount = componentSettings.large_row_count || 3;
    const smallRowCount = componentSettings.small_row_count || 1;
    const largeMaxItems = componentSettings.large_max_items || 10;
    const smallMaxItems = componentSettings.small_max_items || 10;
    const gapSize = componentSettings.gap_size || 'normal';
    const blocks = componentSettings.blocks || [];
     const topTextVariant = componentSettings.top_text_variant || 'strapline-title';

    // Parse HTML content to extract block configuration
    const parseHTMLContent = (htmlContent) => {
        if (!htmlContent) return null;
        
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            
            const outerDiv = doc.querySelector('.structure-fullwidth.ukies-text-with-blocks');
            if (!outerDiv) return null;
            
            const config = {
                reference_id: outerDiv.getAttribute('id') || '',
                strapline: '',
                title: '',
                body: '',
                background_color: 'white',
                block_type: 'tile:var-1',
                large_row_count: 3,
                small_row_count: 1,
                large_max_items: 10,
                small_max_items: 10,
                gap_size: 'normal',
                blocks: []
            };

            // Extract background color from style attribute
            const bgStyle = outerDiv.getAttribute('style');
            if (bgStyle) {
                const bgColorMatch = bgStyle.match(/background-color:\s*([^;]+)/);
                if (bgColorMatch) {
                    const bgColorValue = bgColorMatch[1].trim();
                    if (bgColorValue === '#ffffff' || bgColorValue === 'white') {
                        config.background_color = 'white';
                    } else if (bgColorValue.includes('--ukies-superlight-green') || bgColorValue.includes('#e6ebea')) {
                        config.background_color = 'light-green';
                    }
                }
            }

            // Determine top text variant and extract text content
            config.top_text_variant = 'strapline-title';
            const topStructure = outerDiv.querySelector('.structure.structure-1');
            if (topStructure) {
                const simpleTitleEl = topStructure.querySelector('.component-title');
                const textComponent = topStructure.querySelector('.component-text');
                if (simpleTitleEl) {
                    config.top_text_variant = 'simple-title';
                    config.title = simpleTitleEl.textContent.trim();
                    config.strapline = '';
                    config.body = '';
                } else if (textComponent) {
                    config.top_text_variant = 'strapline-title';
                    const straplineEl = textComponent.querySelector('.preheader-text');
                    const titleEl = textComponent.querySelector('.header-text');
                    const bodyEl = textComponent.querySelector('.body-text');
                    config.strapline = straplineEl ? straplineEl.textContent.trim() : '';
                    config.title = titleEl ? titleEl.textContent.trim() : '';
                    config.body = bodyEl ? bodyEl.innerHTML.trim() : '';
                }
            } else {
                // No top structure present
                config.top_text_variant = 'none';
                config.strapline = '';
                config.title = config.title || '';
                config.body = '';
            }

            // Extract blocks structure settings - find the structure that contains tiles
            const allStructures = Array.from(doc.querySelectorAll('.structure'));
            const topStructureRef = topStructure || null;
            const blockStructure = allStructures.find(s => s !== topStructureRef && s.querySelector('.tile')) || null;
            if (blockStructure) {
                const classes = Array.from(blockStructure.classList);
                
                // Extract row counts
                const lgStructureClass = classes.find(c => c.startsWith('lg:structure-'));
                const structureClass = classes.find(c => c.startsWith('structure-') && !c.startsWith('lg:'));
                
                if (lgStructureClass) {
                    config.large_row_count = parseInt(lgStructureClass.replace('lg:structure-', '')) || 3;
                }
                if (structureClass && structureClass !== 'structure-fullwidth') {
                    config.small_row_count = parseInt(structureClass.replace('structure-', '')) || 1;
                }

                // Extract max items
                const lgMaxItemsClass = classes.find(c => c.startsWith('lg:max-items-'));
                const maxItemsClass = classes.find(c => c.startsWith('max-items-') && !c.startsWith('lg:'));
                
                if (lgMaxItemsClass) {
                    config.large_max_items = parseInt(lgMaxItemsClass.replace('lg:max-items-', '')) || 10;
                }
                if (maxItemsClass) {
                    config.small_max_items = parseInt(maxItemsClass.replace('max-items-', '')) || 10;
                }

                // Check for gap size
                if (classes.includes('gap-small')) {
                    config.gap_size = 'small';
                }
            }

            // Extract blocks
            const tiles = doc.querySelectorAll('.tile');
            tiles.forEach((tile, index) => {
                const tileClasses = Array.from(tile.classList);
                const blockTypeClass = tileClasses.find(c => c.startsWith('tile:var-'));
                
                const block = {
                    id: Date.now() + index,
                    link: tile.getAttribute('href') || '',
                    image_src: '',
                    title: '',
                    subtitle: '',
                    description: '',
                    price: '',
                    price_detail: '',
                    
                };

                // Extract block type from the first tile
                if (index === 0 && blockTypeClass) {
                    config.block_type = blockTypeClass;
                }

                // Extract image
                const img = tile.querySelector('img');
                if (img) {
                    block.image_src = img.getAttribute('src') || '';
                } else {
                    // Check for background image
                    const bgImage = tile.style.backgroundImage;
                    if (bgImage) {
                        const match = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
                        if (match) {
                            block.image_src = match[1];
                        }
                    }
                }

                // Extract text content
                const headerEl = tile.querySelector('.tile-header');
                const subheaderEl = tile.querySelector('.tile-subheader');
                const descriptionEl = tile.querySelector('.tile-description');
                const priceAmountEl = tile.querySelector('.tile-price-amount');
                const priceDetailEl = tile.querySelector('.tile-price-detail');
                block.title = headerEl ? headerEl.textContent.trim() : '';
                block.subtitle = subheaderEl ? subheaderEl.textContent.trim() : '';
                block.description = descriptionEl ? descriptionEl.innerHTML.trim() : '';
                block.price = priceAmountEl ? priceAmountEl.textContent.trim() : '';
                block.price_detail = priceDetailEl ? priceDetailEl.textContent.trim() : '';

                config.blocks.push(block);
            });
            
            return config;
        } catch (error) {
            console.error('Error parsing HTML content:', error);
            return null;
        }
    };

    // HTML escaping function
    const escapeHtml = (text) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    // Generate HTML content from current form values
    const generateHTMLContent = () => {
        return generateHTMLContentWithSettings(componentSettings);
    };

    // Generate HTML content with specific settings
    const generateHTMLContentWithSettings = (settings) => {
        const refId = settings.reference_id || referenceId;
        const straplineText = settings.strapline || strapline;
        const titleText = settings.title || title;
        const bodyText = settings.body || body;
        const backgroundColorValue = settings.background_color || backgroundColor;
        const blockTypeValue = settings.block_type || blockType;
        const largeRowCountValue = settings.large_row_count || largeRowCount;
        const smallRowCountValue = settings.small_row_count || smallRowCount;
        const largeMaxItemsValue = settings.large_max_items || largeMaxItems;
        const smallMaxItemsValue = settings.small_max_items || smallMaxItems;
        const gapSizeValue = settings.gap_size || gapSize;
        const blocksData = settings.blocks || blocks;
        const topTextVariantValue = settings.top_text_variant || topTextVariant || 'strapline-title';

        // Build structure classes
        const structureClasses = [
            'structure',
            `structure-${smallRowCountValue}`,
            `lg:structure-${largeRowCountValue}`
        ];

        if (smallMaxItemsValue < 10) {
            structureClasses.push(`max-items-${smallMaxItemsValue}`);
        }
        if (largeMaxItemsValue < 10) {
            structureClasses.push(`lg:max-items-${largeMaxItemsValue}`);
        }
        if (gapSizeValue === 'small') {
            structureClasses.push('gap-small');
        }

        // Generate blocks HTML
        const blocksHTML = blocksData.map(block => {
            let priceHTML = '';
            if (block.price || block.price_detail) {
                priceHTML = `                <div class="tile-price">
                    <span class="tile-price-amount">${escapeHtml(block.price || '')}</span><br />
                    <span class="tile-price-detail">${escapeHtml(block.price_detail || '')}</span>
                </div>`;
            }

            const linkStart = block.link ? `<a class="tile ${blockTypeValue}"${block.image_src ? ` style="background-image: url(${block.image_src})"` : ''} href="${escapeHtml(block.link)}">` : `<div class="tile ${blockTypeValue}"${block.image_src ? ` style="background-image: url(${block.image_src})"` : ''}>`;
            const linkEnd = block.link ? '</a>' : '</div>';

            // Different structures based on block type
            if (blockTypeValue === 'tile:var-4') {
                // High blocks with image and details sections
                return `        ${linkStart}
            <div class="tile-image component-image">
                <img src="${block.image_src || ''}" />
            </div>
            <div class="tile-details component-text">
                <div class="tile-header">${escapeHtml(block.title || '')}</div>
                <div class="tile-subheader">${escapeHtml(block.subtitle || '')}</div>
                <div class="tile-description">
                    ${block.description || ''}
                </div>
${priceHTML}
            </div>
        ${linkEnd}`;
            } else if (blockTypeValue === 'tile:var-3') {
                // Product tiles with image above details
                return `        ${linkStart}
            <div class="tile-image component-image">
                <img src="${block.image_src || ''}" />
            </div>
            <div class="tile-details">
                <div class="tile-header">${escapeHtml(block.title || '')}</div>
                <div class="tile-subheader">${escapeHtml(block.subtitle || '')}</div>
                <div class="tile-description">
                    ${block.description || ''}
                </div>
${priceHTML}
            </div>
        ${linkEnd}`;
            } else {
                // Simple tile structure for other variants
                return `        ${linkStart}
            <div class="tile-details">
                <div class="tile-header">${escapeHtml(block.title || '')}</div>
                <div class="tile-subheader">${escapeHtml(block.subtitle || '')}</div>
                <div class="tile-description">
                    ${block.description || ''}
                </div>
${priceHTML}
            </div>
        ${linkEnd}`;
            }
        }).join('\n');

        // Generate background color style
        const backgroundStyle = backgroundColorValue === 'light-green' 
            ? ' style="background-color: var(--ukies-superlight-green);"'
            : backgroundColorValue === 'white' ? ' style="background-color: #ffffff;"' : '';

        // Generate classes for the outer div
        const outerClasses = `structure-fullwidth ukies-text-with-blocks${refId ? ' anchor' : ''}`;

        const topSectionHTML = topTextVariantValue === 'none' 
            ? '' 
            : (topTextVariantValue === 'simple-title'
                ? `    <div class="structure structure-1 pb-6">
        <div class="component-title">
            ${escapeHtml(titleText)}
        </div>
    </div>`
                : `    <div class="structure structure-1">
        <div class="component-text center px-10vw">
            <div class="preheader-text">${escapeHtml(straplineText)}</div>
            <div class="header-text">${escapeHtml(titleText)}</div>
            <div class="body-text">
                ${bodyText}
            </div>
        </div>
    </div>`);

        const htmlContent = `<div class="${outerClasses}"${refId ? ` id="${escapeHtml(refId)}"` : ''}${backgroundStyle}>
${topSectionHTML}
    <div class="${structureClasses.join(' ')}">
${blocksHTML}
    </div>
</div>`;

        return htmlContent;
    };

    // Initialize form values from HTML content on component mount
    useEffect(() => {
        if (selectedComponent?.content && !isInitialized) {
            const parsed = parseHTMLContent(selectedComponent.content);
            if (parsed) {
                setComponentSettings(prev => ({
                    ...prev,
                    ...parsed
                }));
            }
            setIsInitialized(true);
        }
    }, [selectedComponent?.content, isInitialized, setComponentSettings]);

    // Reset initialization when component changes
    useEffect(() => {
        setIsInitialized(false);
        setBlockDescriptionSourceCodeModes({});
        setDraggedIndex(null);
        setDragOverIndex(null);
        setDropIndicatorPosition(null);
        // Initialize all accordions as closed by default
        setOpenBlockAccordions([]);
        setPreDragAccordionState([]);
        blockDescriptionRefs.current = {};
    }, [selectedComponent?.id, blocks.length]);

    // Update HTML content when settings change
    const updateHTMLContent = () => {
        const newContent = generateHTMLContent();
        
        setComponentSettings(prev => ({
            ...prev,
            content: newContent
        }));
        
        onChange('content', newContent);
    };

    // Update HTML content with specific settings (for immediate updates)
    const updateHTMLContentWithSettings = (settings) => {
        const newContent = generateHTMLContentWithSettings(settings);
        
        setComponentSettings(prev => ({
            ...prev,
            content: newContent
        }));
        
        onChange('content', newContent);
    };

    // Handle basic field changes
    const handleFieldChange = (key, value) => {
        const newComponentSettings = {
            ...componentSettings,
            [key]: value
        };
        
        setComponentSettings(newComponentSettings);
        
        setTimeout(() => {
            updateHTMLContentWithSettings(newComponentSettings);
        }, 0);
    };

    // Handle block changes
    const handleBlockChange = (blockIndex, key, value) => {
        const newBlocks = [...blocks];
        newBlocks[blockIndex] = {
            ...newBlocks[blockIndex],
            [key]: value
        };
        
        const newComponentSettings = {
            ...componentSettings,
            blocks: newBlocks
        };
        
        setComponentSettings(newComponentSettings);
        
        setTimeout(() => {
            updateHTMLContentWithSettings(newComponentSettings);
        }, 0);
    };

    // Add new block
    const handleAddBlock = () => {
        const newBlock = {
            id: Date.now(),
            link: '',
            image_src: '',
            title: '',
            subtitle: '',
            description: '',
            price: '',
            price_detail: '',
            
        };
        
        const newBlocks = [...blocks, newBlock];
        const newComponentSettings = {
            ...componentSettings,
            blocks: newBlocks
        };
        
        setComponentSettings(newComponentSettings);
        
        setTimeout(() => {
            updateHTMLContentWithSettings(newComponentSettings);
        }, 0);
    };

    // Remove block
    const handleRemoveBlock = (blockIndex) => {
        if (!window.confirm('Weet je zeker dat je dit blok wilt verwijderen?')) {
            return;
        }
        
        const newBlocks = blocks.filter((_, index) => index !== blockIndex);
        const newComponentSettings = {
            ...componentSettings,
            blocks: newBlocks
        };
        
        setComponentSettings(newComponentSettings);
        
        setTimeout(() => {
            updateHTMLContentWithSettings(newComponentSettings);
        }, 0);
    };

    // Handle asset selection
    const handleAssetSelect = (asset) => {
        const imageUrl = asset.image_variations?.large 
            ? asset.image_variations.large.replace('/large', '/max')
            : asset.url;
        
        if (selectedBlockIndex !== null) {
            handleBlockChange(selectedBlockIndex, 'image_src', imageUrl);
        }
        setShowAssetSelector(false);
        setSelectedBlockIndex(null);
    };

    // Handle drag and drop
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target.outerHTML);
        
        // Save current accordion state before closing them for drag
        setPreDragAccordionState(openBlockAccordions);
        
        // Close all accordions during drag
        setOpenBlockAccordions([]);
        
        // Add some styling to the drag image
        e.target.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedIndex(null);
        setDragOverIndex(null);
        setDropIndicatorPosition(null);
        
        // Keep accordions closed after drag for cleaner interface
        setOpenBlockAccordions([]);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (draggedIndex !== null && index !== draggedIndex) {
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const height = rect.height;
            
            // Determine if we're in the top or bottom half
            const position = y < height / 2 ? 'before' : 'after';
            
            setDragOverIndex(index);
            setDropIndicatorPosition(position);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
        setDropIndicatorPosition(null);
    };

    // Handle drag over for the drop zone after the last item
    const handleLastDropZoneDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (draggedIndex !== null) {
            setDragOverIndex(blocks.length - 1);
            setDropIndicatorPosition('after');
        }
    };

    const handleLastDropZoneDragLeave = () => {
        setDragOverIndex(null);
        setDropIndicatorPosition(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        
        if (draggedIndex === null) {
            return;
        }

        const newBlocks = [...blocks];
        const draggedBlock = newBlocks[draggedIndex];
        
        // Remove dragged block from its current position
        newBlocks.splice(draggedIndex, 1);
        
        // Calculate the new insert position
        let insertIndex;
        if (dropIndicatorPosition === 'before') {
            insertIndex = dropIndex;
            if (draggedIndex < dropIndex) {
                insertIndex = dropIndex - 1;
            }
        } else { // 'after'
            insertIndex = dropIndex + 1;
            if (draggedIndex < dropIndex) {
                insertIndex = dropIndex;
            }
        }
        
        // Ensure insert index is within bounds
        insertIndex = Math.max(0, Math.min(insertIndex, newBlocks.length));
        
        // Insert at the new position
        newBlocks.splice(insertIndex, 0, draggedBlock);
        
        const newComponentSettings = {
            ...componentSettings,
            blocks: newBlocks
        };
        
        setComponentSettings(newComponentSettings);
        setDraggedIndex(null);
        setDragOverIndex(null);
        setDropIndicatorPosition(null);
        
        setTimeout(() => {
            updateHTMLContentWithSettings(newComponentSettings);
        }, 0);
    };

    // Handle drop for the last drop zone
    const handleLastDropZoneDrop = (e) => {
        e.preventDefault();
        
        if (draggedIndex === null) {
            return;
        }

        const newBlocks = [...blocks];
        const draggedBlock = newBlocks[draggedIndex];
        
        // Remove dragged block from its current position
        newBlocks.splice(draggedIndex, 1);
        
        // Insert at the end
        newBlocks.push(draggedBlock);
        
        const newComponentSettings = {
            ...componentSettings,
            blocks: newBlocks
        };
        
        setComponentSettings(newComponentSettings);
        setDraggedIndex(null);
        setDragOverIndex(null);
        setDropIndicatorPosition(null);
        
        setTimeout(() => {
            updateHTMLContentWithSettings(newComponentSettings);
        }, 0);
    };

    // Rich text editor functions
    const formatBodyText = (command, value = null) => {
        document.execCommand(command, false, value);
        bodyEditorRef.current.focus();
    };

    const formatBlockDescriptionText = (blockIndex, command, value = null) => {
        document.execCommand(command, false, value);
        if (blockDescriptionRefs.current[blockIndex]) {
            blockDescriptionRefs.current[blockIndex].focus();
        }
    };

    // Handle body text change
    const handleBodyTextChange = (e) => {
        const content = e.target.innerHTML;
        if (content === '<p>Voer hier de tekst in...</p>') {
            handleFieldChange('body', '');
        } else {
            handleFieldChange('body', content);
        }
    };

    // Handle block description text change
    const handleBlockDescriptionTextChange = (blockIndex, e) => {
        const content = e.target.innerHTML;
        if (content === '<p>Voer hier de beschrijving in...</p>') {
            handleBlockChange(blockIndex, 'description', '');
        } else {
            handleBlockChange(blockIndex, 'description', content);
        }
    };

    // Handle editor clicks to clear placeholder
    const handleBodyEditorClick = () => {
        if (bodyEditorRef.current && bodyEditorRef.current.innerHTML === '<p>Voer hier de tekst in...</p>') {
            bodyEditorRef.current.innerHTML = '<p><br></p>';
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStart(bodyEditorRef.current.firstChild, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    const handleBlockDescriptionEditorClick = (blockIndex) => {
        const ref = blockDescriptionRefs.current[blockIndex];
        if (ref && ref.innerHTML === '<p>Voer hier de beschrijving in...</p>') {
            ref.innerHTML = '<p><br></p>';
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStart(ref.firstChild, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    // Source code mode toggles
    const handleBodySourceCodeToggle = () => {
        if (bodySourceCodeMode) {
            const sourceContent = bodyEditorRef.current.value;
            handleFieldChange('body', sourceContent);
        } else {
            const wysiwygContent = bodyEditorRef.current.innerHTML;
            handleFieldChange('body', wysiwygContent);
        }
        setBodySourceCodeMode(!bodySourceCodeMode);
    };

    const handleBlockDescriptionSourceCodeToggle = (blockIndex) => {
        const isSourceMode = blockDescriptionSourceCodeModes[blockIndex];
        
        if (isSourceMode) {
            const sourceContent = blockDescriptionRefs.current[blockIndex].value;
            handleBlockChange(blockIndex, 'description', sourceContent);
        } else {
            const wysiwygContent = blockDescriptionRefs.current[blockIndex].innerHTML;
            handleBlockChange(blockIndex, 'description', wysiwygContent);
        }
        
        setBlockDescriptionSourceCodeModes(prev => ({
            ...prev,
            [blockIndex]: !isSourceMode
        }));
    };

    // Set initial content for editors when data changes
    useEffect(() => {
        if (bodyEditorRef.current && body !== undefined) {
            const isFocused = document.activeElement === bodyEditorRef.current;
            
            if (!isFocused) {
                if (bodySourceCodeMode) {
                    bodyEditorRef.current.value = body || '';
                } else {
                    bodyEditorRef.current.innerHTML = body || '<p>Voer hier de tekst in...</p>';
                }
            }
        }
    }, [body, bodySourceCodeMode]);

    useEffect(() => {
        blocks.forEach((block, index) => {
            const ref = blockDescriptionRefs.current[index];
            if (ref && block.description !== undefined) {
                const isFocused = document.activeElement === ref;
                
                if (!isFocused) {
                    const isSourceMode = blockDescriptionSourceCodeModes[index];
                    if (isSourceMode) {
                        ref.value = block.description || '';
                    } else {
                        ref.innerHTML = block.description || '<p>Voer hier de beschrijving in...</p>';
                    }
                }
            }
        });
    }, [blocks, blockDescriptionSourceCodeModes]);

    const getBlockTypeLabel = (type) => {
        switch(type) {
            case 'tile:var-1': return 'Afbeelding met omhoogkomende tekst';
            case 'tile:var-2': return 'Afbeelding met titel';
            case 'tile:var-3': return 'Product-tegel';
            case 'tile:var-4': return 'Hoge blokken';
            default: return 'Afbeelding met omhoogkomende tekst';
        }
    };

    const getUnusedFields = (type) => {
        switch(type) {
            case 'tile:var-1': return ['price', 'price_detail'];
            case 'tile:var-2': return ['subtitle', 'description', 'price', 'price_detail'];
            case 'tile:var-3': return ['subtitle', 'description'];
            case 'tile:var-4': return ['subtitle'];
            default: return ['price', 'price_detail'];
        }
    };

    const isFieldUnused = (fieldName) => {
        const unusedFields = getUnusedFields(blockType);
        return unusedFields.includes(fieldName);
    };

    return (
        <div>
            <style jsx>{`
                .rich-text-editor {
                    font-family: inherit;
                    font-size: 0.9rem;
                    line-height: 1.5;
                }
                .rich-text-editor:focus {
                    border-color: #80bdff !important;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
                }
                .rich-text-editor p {
                    margin-bottom: 0.5rem;
                }
                .rich-text-editor ul, .rich-text-editor ol {
                    margin-bottom: 0.5rem;
                    padding-left: 1.5rem;
                }
                .rich-text-editor a {
                    color: #007bff;
                    text-decoration: underline;
                }
                .accordion-button {
                    background-color: white !important;
                    border: 1px solid #dee2e6 !important;
                    padding-left: 3rem !important;
                    padding-right: 1.25rem !important;
                }
                .accordion-button:not(.collapsed) {
                    background-color: white !important;
                    color: inherit !important;
                    box-shadow: none !important;
                }
                .accordion-button:focus {
                    box-shadow: none !important;
                    border-color: #dee2e6 !important;
                }
                .accordion-button::after {
                    display: none !important;
                }
                .accordion-button::before {
                    content: '';
                    position: absolute;
                    left: 1.25rem;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 0.5rem;
                    height: 0.5rem;
                    border-right: 2px solid currentColor;
                    border-bottom: 2px solid currentColor;
                    transform: translateY(-50%) rotate(45deg);
                    transition: transform 0.2s ease-in-out;
                }
                .accordion-button:not(.collapsed)::before {
                    transform: translateY(-50%) rotate(-135deg);
                }
                .drag-handle {
                    cursor: grab;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 4px;
                    background-color: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 4px;
                    transition: background-color 0.2s;
                    width: 32px;
                    height: 32px;
                }
                .drag-handle:hover {
                    background-color: #e9ecef;
                }
                .drag-handle:active {
                    cursor: grabbing;
                }
                .drag-handle-arrows {
                    display: flex;
                    gap: 2px;
                    font-size: 14px;
                    line-height: 1;
                    color: var(--ukies-dark-green);
                    font-weight: bold;
                }
                .dragging {
                    opacity: 0.5;
                }
                .drag-over {
                    background-color: #fff5e6;
                }
                .drag-over .accordion-button {
                    background-color: #fff5e6 !important;
                    border-bottom: 2px solid var(--ukies-dark-orange);
                }
                .drop-indicator {
                    height: 3px;
                    background-color: var(--ukies-dark-orange);
                    border-radius: 2px;
                    margin: 2px 0;
                    opacity: 0;
                    transition: opacity 0.2s;
                }
                .drop-indicator.active {
                    opacity: 1;
                }
                .drop-zone-last {
                    height: 20px;
                    border: 2px dashed transparent;
                    border-radius: 4px;
                    margin: 10px 0;
                    transition: border-color 0.2s;
                }
                .drop-zone-last.drag-over {
                    border-color: var(--ukies-dark-orange);
                    background-color: rgba(255, 165, 0, 0.1);
                }
                .block-controls {
                    background-color: #f8f9fa;
                }
            `}</style>
            
            <Row className="mb-3">
                <Col md={8}>
                    <Form.Group>
                        <Form.Label>Referentie-ID</Form.Label>
                        <Form.Control
                            type="text"
                            value={referenceId}
                            onChange={(e) => handleFieldChange('reference_id', e.target.value)}
                            placeholder="Voer referentie-ID in (optioneel)"
                        />
                        <Form.Text className="text-muted">
                            Gebruik dit als je vanuit ergens anders naar dit blok wilt linken. Voeg #jouw-referentie-ID toe aan het einde van de link.
                        </Form.Text>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Achtergrondkleur</Form.Label>
                        <Form.Select
                            value={backgroundColor}
                            onChange={(e) => handleFieldChange('background_color', e.target.value)}
                        >
                            <option value="white">Wit</option>
                            <option value="light-green">Lichtgroen</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {/* Tekst variant */}
            <Form.Group className="mb-3">
                <Form.Label>Tekst variant</Form.Label>
                <Form.Select
                    value={topTextVariant}
                    onChange={(e) => handleFieldChange('top_text_variant', e.target.value)}
                >
                    <option value="none">Geen tekst</option>
                    <option value="strapline-title">Strapline + titel</option>
                    <option value="simple-title">Simpele titel</option>
                </Form.Select>
            </Form.Group>

            {topTextVariant === 'strapline-title' && (
                <Form.Group className="mb-3">
                    <Form.Label>Strapline</Form.Label>
                    <Form.Control
                        type="text"
                        value={strapline}
                        onChange={(e) => handleFieldChange('strapline', e.target.value)}
                        placeholder="Voer strapline in"
                    />
                </Form.Group>
            )}
            
            {(topTextVariant === 'strapline-title' || topTextVariant === 'simple-title') && (
                <Form.Group className="mb-3">
                    <Form.Label>Titel</Form.Label>
                    <Form.Control
                        type="text"
                        value={title}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        placeholder="Voer titel in"
                    />
                </Form.Group>
            )}
            
            {topTextVariant === 'strapline-title' && (
                <Form.Group className="mb-3">
                    <Form.Label>Tekst</Form.Label>
                    
                    {/* Rich Text Editor Toolbar */}
                    <div className="border rounded-top p-2 bg-light">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => formatBodyText('bold')}
                            disabled={bodySourceCodeMode}
                        >
                            <strong>B</strong>
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => formatBodyText('italic')}
                            disabled={bodySourceCodeMode}
                        >
                            <em>I</em>
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => formatBodyText('underline')}
                            disabled={bodySourceCodeMode}
                        >
                            <u>U</u>
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => formatBodyText('insertUnorderedList')}
                            disabled={bodySourceCodeMode}
                        >
                            • List
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => formatBodyText('insertOrderedList')}
                            disabled={bodySourceCodeMode}
                        >
                            1. List
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                                const url = prompt('Enter URL:');
                                if (url) formatBodyText('createLink', url);
                            }}
                            disabled={bodySourceCodeMode}
                        >
                            🔗 Link
                        </Button>
                        <Button
                            variant={bodySourceCodeMode ? "primary" : "outline-secondary"}
                            size="sm"
                            className="ms-auto"
                            onClick={handleBodySourceCodeToggle}
                        >
                            {bodySourceCodeMode ? "WYSIWYG" : "</>"}
                        </Button>
                    </div>
                    
                    {/* Editor Area */}
                    {bodySourceCodeMode ? (
                        <Form.Control
                            ref={bodyEditorRef}
                            as="textarea"
                            rows={6}
                            value={body || ''}
                            onChange={(e) => handleFieldChange('body', e.target.value)}
                            style={{
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                borderTop: 'none',
                                borderTopLeftRadius: '0',
                                borderTopRightRadius: '0'
                            }}
                            placeholder="Voer hier de HTML-code in..."
                        />
                    ) : (
                        <div
                            ref={bodyEditorRef}
                            contentEditable="true"
                            onInput={handleBodyTextChange}
                            onClick={handleBodyEditorClick}
                            className="rich-text-editor"
                            style={{
                                minHeight: '150px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                border: '1px solid #ced4da',
                                borderTop: 'none',
                                borderBottomLeftRadius: '0.25rem',
                                borderBottomRightRadius: '0.25rem',
                                padding: '0.75rem',
                                backgroundColor: 'white',
                                outline: 'none'
                            }}
                            suppressContentEditableWarning={true}
                        />
                    )}
                </Form.Group>
            )}

            {/* Block Layout Settings */}
            <h6 className="mb-2 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
                Blok instellingen
            </h6>

            <Form.Group className="mb-3">
                <Form.Label>Type blokken</Form.Label>
                <Form.Select
                    value={blockType}
                    onChange={(e) => handleFieldChange('block_type', e.target.value)}
                >
                    <option value="tile:var-1">Afbeelding met omhoogkomende tekst</option>
                    <option value="tile:var-2">Afbeelding met titel</option>
                    <option value="tile:var-3">Product-tegel</option>
                    <option value="tile:var-4">Hoge blokken</option>
                </Form.Select>
            </Form.Group>

            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Blokken per rij (grote schermen)</Form.Label>
                        <Form.Select
                            value={largeRowCount}
                            onChange={(e) => handleFieldChange('large_row_count', parseInt(e.target.value))}
                        >
                            <option value={1}>1</option>
                            <option value={2}>2</option>
                            <option value={3}>3</option>
                            <option value={4}>4</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Blokken per rij (kleine schermen)</Form.Label>
                        <Form.Select
                            value={smallRowCount}
                            onChange={(e) => handleFieldChange('small_row_count', parseInt(e.target.value))}
                        >
                            <option value={1}>1</option>
                            <option value={2}>2</option>
                            <option value={3}>3</option>
                            <option value={4}>4</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Max zichtbare items (grote schermen)</Form.Label>
                        <Form.Select
                            value={largeMaxItems}
                            onChange={(e) => handleFieldChange('large_max_items', parseInt(e.target.value))}
                        >
                            {[...Array(10)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Max zichtbare items (kleine schermen)</Form.Label>
                        <Form.Select
                            value={smallMaxItems}
                            onChange={(e) => handleFieldChange('small_max_items', parseInt(e.target.value))}
                        >
                            {[...Array(10)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Ruimte tussen de blokken</Form.Label>
                <Form.Select
                    value={gapSize}
                    onChange={(e) => handleFieldChange('gap_size', e.target.value)}
                >
                    <option value="normal">Normaal</option>
                    <option value="small">Klein</option>
                </Form.Select>
            </Form.Group>

            {/* Blocks Configuration */}
            <h6 className="mb-2 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
                Blokken configuratie
            </h6>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">Sleep blokken om volgorde te wijzigen</span>
                <Button 
                    variant="success" 
                    onClick={handleAddBlock}
                    size="sm"
                >
                    + Blok toevoegen
                </Button>
            </div>

            <Accordion 
                className="my-4"
                activeKey={openBlockAccordions}
                onSelect={(eventKey) => {
                    // Prevent accordion toggle when dragging
                    if (draggedIndex === null) {
                        setOpenBlockAccordions(prev => 
                            prev.includes(eventKey) 
                                ? prev.filter(key => key !== eventKey)
                                : [...prev, eventKey]
                        );
                    }
                }}
                style={{borderTop: blocks.length > 0 ? '2px solid #dee2e6' : 'none'}}
            >
                {blocks.map((block, index) => (
                    <Fragment key={block.id}>
                        {/* Drop indicator before item */}
                        <div 
                            className={`drop-indicator ${
                                dragOverIndex === index && dropIndicatorPosition === 'before' ? 'active' : ''
                            }`}
                        />
                        
                        <Accordion.Item 
                            eventKey={index.toString()}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            className={`${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                        >
                            <Accordion.Header>
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <div className="flex-grow-1 me-3">
                                        <strong>Blok {index + 1}{block.title ? ` - ${block.title}` : ''}</strong>
                                    </div>
                                    <div className="d-flex gap-3 align-items-center">
                                        <div 
                                            className="drag-handle" 
                                            title="Sleep om blok te verplaatsen"
                                            draggable="true"
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragEnd={handleDragEnd}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            <div className="drag-handle-arrows">
                                                <span>↑</span>
                                                <span>↓</span>
                                            </div>
                                        </div>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveBlock(index);
                                            }}
                                            title="Verwijder blok"
                                            style={{ 
                                                backgroundColor: '#dc3545',
                                                borderColor: '#dc3545',
                                                color: 'white',
                                                width: '32px',
                                                height: '32px'
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </Button>
                                    </div>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="block-controls">
                                {/* Titel */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Titel</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={block.title || ''}
                                        onChange={(e) => handleBlockChange(index, 'title', e.target.value)}
                                        placeholder="Voer titel in"
                                    />
                                </Form.Group>

                                {/* Subtitel */}
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: isFieldUnused('subtitle') ? 'var(--ukies-disabled)' : 'inherit' }}>
                                        Subtitel
                                        {isFieldUnused('subtitle') && (
                                            <span 
                                                className="ms-2"
                                                style={{ 
                                                    color: 'var(--ukies-disabled)',
                                                    cursor: 'help',
                                                    fontSize: '14px'
                                                }}
                                                title="Wordt niet getoond bij huidige blok-type"
                                            >
                                                ✗
                                            </span>
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={block.subtitle || ''}
                                        onChange={(e) => handleBlockChange(index, 'subtitle', e.target.value)}
                                        placeholder="Voer subtitel in (optioneel)"
                                        style={{ 
                                            borderColor: isFieldUnused('subtitle') ? 'var(--ukies-disabled)' : '#ced4da'
                                        }}
                                    />
                                </Form.Group>

                                {/* Beschrijving */}
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: isFieldUnused('description') ? 'var(--ukies-disabled)' : 'inherit' }}>
                                        Beschrijving
                                        {isFieldUnused('description') && (
                                            <span 
                                                className="ms-2"
                                                style={{ 
                                                    color: 'var(--ukies-disabled)',
                                                    cursor: 'help',
                                                    fontSize: '14px'
                                                }}
                                                title="Wordt niet getoond bij huidige blok-type"
                                            >
                                                ✗
                                            </span>
                                        )}
                                    </Form.Label>
                                    
                                    {/* Rich Text Editor Toolbar */}
                                    <div 
                                        className="rounded-top p-2 bg-light" 
                                        style={{ 
                                            border: 'var(--bs-border-width) var(--bs-border-style) var(--bs-border-color)',
                                            borderColor: isFieldUnused('description') ? 'var(--ukies-disabled)' : '#ced4da'
                                        }}
                                    >
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => formatBlockDescriptionText(index, 'bold')}
                                            disabled={blockDescriptionSourceCodeModes[index]}
                                        >
                                            <strong>B</strong>
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => formatBlockDescriptionText(index, 'italic')}
                                            disabled={blockDescriptionSourceCodeModes[index]}
                                        >
                                            <em>I</em>
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => formatBlockDescriptionText(index, 'underline')}
                                            disabled={blockDescriptionSourceCodeModes[index]}
                                        >
                                            <u>U</u>
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => formatBlockDescriptionText(index, 'insertUnorderedList')}
                                            disabled={blockDescriptionSourceCodeModes[index]}
                                        >
                                            • List
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => formatBlockDescriptionText(index, 'insertOrderedList')}
                                            disabled={blockDescriptionSourceCodeModes[index]}
                                        >
                                            1. List
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => {
                                                const url = prompt('Enter URL:');
                                                if (url) formatBlockDescriptionText(index, 'createLink', url);
                                            }}
                                            disabled={blockDescriptionSourceCodeModes[index]}
                                        >
                                            🔗 Link
                                        </Button>
                                        <Button
                                            variant={blockDescriptionSourceCodeModes[index] ? "primary" : "outline-secondary"}
                                            size="sm"
                                            className="ms-auto"
                                            onClick={() => handleBlockDescriptionSourceCodeToggle(index)}
                                        >
                                            {blockDescriptionSourceCodeModes[index] ? "WYSIWYG" : "</>"}
                                        </Button>
                                    </div>
                                    
                                    {/* Editor Area */}
                                    {blockDescriptionSourceCodeModes[index] ? (
                                        <Form.Control
                                            ref={el => blockDescriptionRefs.current[index] = el}
                                            as="textarea"
                                            rows={4}
                                            value={block.description || ''}
                                            onChange={(e) => handleBlockChange(index, 'description', e.target.value)}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.9rem',
                                                borderTop: 'none',
                                                borderTopLeftRadius: '0',
                                                borderTopRightRadius: '0',
                                                borderColor: isFieldUnused('description') ? 'var(--ukies-disabled)' : '#ced4da'
                                            }}
                                            placeholder="Voer hier de HTML-code in..."
                                        />
                                    ) : (
                                        <div
                                            ref={el => blockDescriptionRefs.current[index] = el}
                                            contentEditable="true"
                                            onInput={(e) => handleBlockDescriptionTextChange(index, e)}
                                            onClick={() => handleBlockDescriptionEditorClick(index)}
                                            className="rich-text-editor"
                                            style={{
                                                minHeight: '120px',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                border: `1px solid ${isFieldUnused('description') ? 'var(--ukies-disabled)' : '#ced4da'}`,
                                                borderTop: 'none',
                                                borderBottomLeftRadius: '0.25rem',
                                                borderBottomRightRadius: '0.25rem',
                                                padding: '0.75rem',
                                                backgroundColor: 'white',
                                                outline: 'none'
                                            }}
                                            suppressContentEditableWarning={true}
                                        />
                                    )}
                                </Form.Group>

                                {/* Price fields - show for all block types */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isFieldUnused('price') ? 'var(--ukies-disabled)' : 'inherit' }}>
                                                Prijs
                                                {isFieldUnused('price') && (
                                                    <span 
                                                        className="ms-2"
                                                        style={{ 
                                                            color: 'var(--ukies-disabled)',
                                                            cursor: 'help',
                                                            fontSize: '14px'
                                                        }}
                                                        title="Wordt niet getoond bij huidige blok-type"
                                                    >
                                                        ✗
                                                    </span>
                                                )}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={block.price || ''}
                                                onChange={(e) => handleBlockChange(index, 'price', e.target.value)}
                                                placeholder="Voer prijs in (bijv. € 99,-)"
                                                style={{ 
                                                    borderColor: isFieldUnused('price') ? 'var(--ukies-disabled)' : '#ced4da'
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label style={{ color: isFieldUnused('price_detail') ? 'var(--ukies-disabled)' : 'inherit' }}>
                                                Prijs detail
                                                {isFieldUnused('price_detail') && (
                                                    <span 
                                                        className="ms-2"
                                                        style={{ 
                                                            color: 'var(--ukies-disabled)',
                                                            cursor: 'help',
                                                            fontSize: '14px'
                                                        }}
                                                        title="Wordt niet getoond bij huidige blok-type"
                                                    >
                                                        ✗
                                                    </span>
                                                )}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={block.price_detail || ''}
                                                onChange={(e) => handleBlockChange(index, 'price_detail', e.target.value)}
                                                style={{ 
                                                    borderColor: isFieldUnused('price_detail') ? 'var(--ukies-disabled)' : '#ced4da'
                                                }}
                                                placeholder="Voer prijs detail in"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Link */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Link</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={block.link || ''}
                                        onChange={(e) => handleBlockChange(index, 'link', e.target.value)}
                                        placeholder="Voer link in (optioneel)"
                                    />
                                </Form.Group>

                                {/* Afbeelding */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Afbeelding</Form.Label>
                                    <div className="d-flex align-items-center gap-2">
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                setSelectedBlockIndex(index);
                                                setShowAssetSelector(true);
                                            }}
                                            style={{ 
                                                filter: 'grayscale(100%)',
                                                minWidth: '35.2px',
                                                height: '35.2px',
                                                padding: '0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            🖼️
                                        </Button>
                                        <Form.Control
                                            type="text"
                                            value={block.image_src || ''}
                                            onChange={(e) => handleBlockChange(index, 'image_src', e.target.value)}
                                            placeholder="Afbeelding URL"
                                            disabled
                                        />
                                    </div>
                                    {block.image_src && (
                                        <div className="mt-2">
                                            <img 
                                                src={block.image_src} 
                                                alt="Preview" 
                                                style={{ 
                                                    width: '100%', 
                                                    maxHeight: '120px',
                                                    objectFit: 'cover',
                                                    border: '1px solid #dee2e6',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => window.open(block.image_src, '_blank')}
                                            />
                                        </div>
                                    )}
                                </Form.Group>
                            </Accordion.Body>
                        </Accordion.Item>
                        
                        {/* Drop indicator after item */}
                        <div 
                            className={`drop-indicator ${
                                dragOverIndex === index && dropIndicatorPosition === 'after' ? 'active' : ''
                            }`}
                        />
                    </Fragment>
                ))}
            </Accordion>


            {blocks.length === 0 && (
                <div className="text-center p-4 bg-light rounded">
                    <p className="text-muted mb-2">Nog geen blokken toegevoegd</p>
                    <Button variant="success" onClick={handleAddBlock}>
                        + Eerste blok toevoegen
                    </Button>
                </div>
            )}

            {/* Asset Selector Dialog */}
            <UkiesAssets
                open={showAssetSelector}
                setOpen={setShowAssetSelector}
                onAssetSelect={handleAssetSelect}
                token={apiToken}
            />
        </div>
    );
}